import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, Unlock, Navigation, ScrollText, ArrowRight, Heart, Users,
  Briefcase, Church, Plane, Landmark, ShieldCheck, Clock, Gift, Music,
} from "lucide-react";
import GameplayMockups from "@/components/site/GameplayMockups";
import MapShowcase from "@/components/site/MapShowcase";
import FirstDateMap from "@/components/site/FirstDateMap";

const HERO_IMG = "https://images.unsplash.com/photo-1758613171311-efa470985996?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }),
};

const loopSteps = [
  { icon: ScrollText, title: "Get a clue", desc: "A photo, riddle, poem or video tied to a real place." },
  { icon: MapPin, title: "Drop a pin", desc: "Solve it on the map — or actually go there in person." },
  { icon: Navigation, title: "We check the distance", desc: "Close enough to the secret target? That's a hit." },
  { icon: Unlock, title: "Unlock the story", desc: "A memory, video, photo or gift waiting at that place." },
];

const useCases = [
  { icon: Heart, title: "Couples & Proposals", desc: "Walk them through your whole story — first date, first kiss, ending on one knee.", span: "md:col-span-2", img: "https://images.unsplash.com/photo-1779723204737-189eae74ff14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { icon: Users, title: "Parents & Kids", desc: "Where were you born? Where'd you take your first steps? A journey through their own life.", img: "https://images.unsplash.com/photo-1595981685360-02d21ed6afde?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { icon: Gift, title: "Birthdays", desc: "A trail of meaningful places ending in a message — or a present.", img: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { icon: Briefcase, title: "Team Building", desc: "Company scavenger hunts across the city that people actually talk about after.", img: "https://images.unsplash.com/photo-1592591281836-af5e5904c466?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { icon: Church, title: "Youth Groups", desc: "Safe, guided hunts for church groups, scouts and schools.", img: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { icon: Plane, title: "Travel & Road Trips", desc: "Relive the vacation, one pin at a time.", img: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { icon: Landmark, title: "History & Tourism", desc: "Old photographs, hidden stories, real streets.", span: "md:col-span-2", img: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { icon: Music, title: "Celebrity Walk", desc: "Trace the real places behind legendary lives — starting with John Lennon, from a Liverpool maternity ward to the Dakota. 25 pins across four cities.", span: "md:col-span-3", img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", to: "/play/lennon" },
];

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Friends exploring with a map" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#090A0F]/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090A0F] via-transparent to-[#090A0F]/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20 w-full">
          <motion.div initial="hidden" animate="show" className="max-w-3xl">
            <motion.p variants={fadeUp} custom={0} className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-6 flex items-center gap-2">
              <span className="relative flex w-2.5 h-2.5"><span className="absolute inset-0 rounded-full bg-[#FF453A] pin-pulse" /><span className="relative w-2.5 h-2.5 rounded-full bg-[#FF453A]" /></span>
              The location-based storytelling game
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-white leading-[1.02]">
              Every place<br />has a <span className="text-[#FF453A]">story.</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-6 text-lg text-[#c9cdd8] leading-relaxed max-w-xl">
              Build a quest through the places that matter. Send clues. Watch someone
              you love hunt down the spot, drop a pin — and unlock the memory
              you hid there.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/early-access" data-testid="hero-early-access-btn" className="rounded-full bg-[#FF453A] hover:bg-[#FF6961] transition-colors text-white font-semibold px-8 py-4 text-center glow-red flex items-center justify-center gap-2">
                Get Early Access <ArrowRight size={17} />
              </Link>
              <Link to="/how-it-works" data-testid="hero-how-it-works-btn" className="rounded-full border border-white/20 hover:border-white/50 transition-colors text-white font-semibold px-8 py-4 text-center backdrop-blur">
                See How It Works
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <FirstDateMap />

      {/* GAME LOOP */}
      <section className="py-24 border-t border-[#2A3042]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-4">The game loop</p>
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-white max-w-2xl">
            Clue. Pin. Unlock. Repeat.
          </h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {loopSteps.map((s, i) => (
              <motion.div
                key={s.title}
                variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="rounded-2xl bg-[#131620] border border-[#2A3042] hover:border-[#FF453A] transition-colors hover:-translate-y-1 duration-300 transition-transform p-8"
              >
                <div className="w-11 h-11 rounded-full bg-[#FF453A]/10 border border-[#FF453A]/30 flex items-center justify-center mb-5">
                  <s.icon size={19} className="text-[#FF453A]" />
                </div>
                <p className="font-heading font-semibold text-white text-lg mb-2">{s.title}</p>
                <p className="text-sm text-[#9BA1B0] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PHONE MOCKUPS */}
      <section className="py-24 bg-[#0b0d14] border-y border-[#2A3042] relative grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-4">In their hands</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-white">
              This is what playing feels like
            </h2>
          </div>
          <GameplayMockups />
        </div>
      </section>

      {/* MAP SHOWCASE */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-4">Real maps, real places</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-white">
              Drop a pin from your couch.<br />Or stand on the exact spot.
            </h2>
            <p className="mt-5 text-[#9BA1B0] leading-relaxed">
              Every clue can be solved two ways: find the place on the map and lock in
              your pin, or physically go there and tap "Check My Location."
              Either way counts — and the creator never knows which one you chose.
            </p>
          </div>
          <div className="lg:col-span-3">
            <MapShowcase />
          </div>
        </div>
      </section>

      {/* USE CASES BENTO */}
      <section className="py-24 border-t border-[#2A3042]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-4">Made for moments</p>
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-white">
            One engine. A thousand kinds of quests.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {useCases.map((u, i) => (
              <motion.div
                key={u.title}
                variants={fadeUp} custom={i % 3} initial="hidden" whileInView="show" viewport={{ once: true }}
                className={`relative rounded-2xl bg-[#131620] border border-[#2A3042] hover:border-[#FF453A] transition-colors overflow-hidden group ${u.span || ""}`}
              >
                {u.img && (
                  <div className="absolute inset-0">
                    <img src={u.img} alt={u.title} className="w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#131620] via-[#131620]/70 to-transparent" />
                  </div>
                )}
                <div className="relative p-8 flex flex-col justify-end min-h-[190px]">
                  <u.icon size={20} className="text-[#FF453A] mb-4" />
                  <p className="font-heading font-semibold text-white text-lg mb-1.5">{u.title}</p>
                  <p className="text-sm text-[#9BA1B0] leading-relaxed">{u.desc}</p>
                  {u.to && (
                    <Link to={u.to} data-testid="home-play-lennon-link" className="mt-4 inline-flex items-center gap-2 text-[#FF453A] hover:text-[#FF6961] font-semibold text-sm transition-colors">
                      Play the first 3 pins free <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/use-cases" data-testid="see-all-use-cases-link" className="inline-flex items-center gap-2 text-[#FF453A] hover:text-[#FF6961] font-semibold transition-colors">
              Explore all use cases <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRIVACY TEASER */}
      <section className="py-24 bg-[#0b0d14] border-t border-[#2A3042] relative grain">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#34C759]/10 border border-[#34C759]/30 mb-6">
            <ShieldCheck size={24} className="text-[#34C759]" />
          </div>
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-white">
            A game, never a tracker.
          </h2>
          <p className="mt-5 text-[#9BA1B0] leading-relaxed max-w-2xl mx-auto">
            PinQuests remembers where you guessed — but it never tells the creator where
            you are. All location activity is delayed at least 24 hours, and no one is
            ever told whether you visited in person or guessed from the map.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 text-sm text-[#9BA1B0]">
            <Clock size={15} className="text-[#34C759]" /> 24-hour privacy delay, enforced on our servers
          </div>
          <Link to="/privacy" data-testid="privacy-teaser-link" className="mt-8 inline-flex items-center gap-2 text-[#FF453A] hover:text-[#FF6961] font-semibold transition-colors">
            Read the privacy promise <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 text-center relative overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-[#FF453A]/8 blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
          <MapPin size={36} className="text-[#FF453A] mx-auto mb-6" />
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter text-white">
            Someone's story is waiting<br />to be hidden.
          </h2>
          <p className="mt-5 text-[#9BA1B0]">Claim your spot before we open the map.</p>
          <Link to="/early-access" data-testid="cta-early-access-btn" className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#FF453A] hover:bg-[#FF6961] transition-colors text-white font-semibold px-9 py-4 glow-red">
            Get Early Access <ArrowRight size={17} />
          </Link>
        </div>
      </section>
    </main>
  );
}
